import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServicePreview = ({ onServiceClick }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.8, 1, 1, 0.8]);

  const services = [
    {
      id: 1,
      icon: 'Code2',
      title: 'IT Engineering',
      description: 'Full-stack web and mobile development with cutting-edge technologies. Building scalable, secure, and high-performance applications tailored to your business needs.',
      features: ['Web Development', 'Mobile Apps', 'Cloud Solutions', 'API Integration'],
      color: 'primary',
      gradient: 'from-primary to-secondary'
    },
    {
      id: 2,
      icon: 'TrendingUp',
      title: 'Entrepreneurship',
      description: 'Strategic business consulting and digital transformation guidance. Helping startups and enterprises navigate the digital landscape with proven strategies.',
      features: ['Business Strategy', 'Digital Transformation', 'Market Analysis', 'Growth Planning'],
      color: 'secondary',
      gradient: 'from-secondary to-accent'
    },
    {
      id: 3,
      icon: 'Users',
      title: 'Mentorship',
      description: 'Empowering young professionals and aspiring developers through personalized guidance. Career development, technical skills, and leadership training.',
      features: ['Career Guidance', 'Technical Training', 'Leadership Skills', 'Personal Growth'],
      color: 'accent',
      gradient: 'from-accent to-primary'
    },
    {
      id: 4,
      icon: 'Palette',
      title: 'Creative Direction',
      description: 'Brand identity, creative strategy, and visual storytelling for businesses. Bringing your vision to life through innovative design and compelling narratives.',
      features: ['Brand Identity', 'Visual Design', 'Content Strategy', 'Creative Campaigns'],
      color: 'success',
      gradient: 'from-success to-secondary'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  return (
    <motion.section
      ref={containerRef}
      style={{ opacity, scale }}
      className="py-12 md:py-16 lg:py-24 px-4 md:px-6 lg:px-8 bg-gradient-to-b from-background to-card"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 md:mb-14 lg:mb-20"
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-primary/10 border border-primary/20 mb-4 md:mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Icon name="Sparkles" size={16} color="var(--color-primary)" />
            <span className="text-xs md:text-sm lg:text-base font-medium text-primary">
              What I Offer
            </span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-5 lg:mb-6">
            <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Services & Expertise
            </span>
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
            Comprehensive solutions spanning technology, business, mentorship, and creative excellence
          </p>
        </motion.div>

        {/* Service Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10"
        >
          {services?.map((service, index) => (
            <motion.div
              key={service?.id}
              variants={cardVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group relative"
            >
              <div className="relative h-full p-6 md:p-7 lg:p-8 rounded-2xl bg-card border border-border overflow-hidden transition-all duration-300 hover:border-primary/50 hover:shadow-2xl">
                {/* Animated Background Gradient */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${service?.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                  initial={false}
                />

                {/* Icon Container */}
                <motion.div
                  className="relative w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-5 md:mb-6 lg:mb-7"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <Icon name={service?.icon} size={32} color={`var(--color-${service?.color})`} />
                </motion.div>

                {/* Content */}
                <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3 md:mb-4 text-foreground">
                  {service?.title}
                </h3>
                <p className="text-sm md:text-base lg:text-lg text-muted-foreground mb-5 md:mb-6 leading-relaxed">
                  {service?.description}
                </p>

                {/* Features List */}
                <div className="grid grid-cols-2 gap-2 md:gap-3 mb-5 md:mb-6">
                  {service?.features?.map((feature, idx) => (
                    <motion.div
                      key={idx}
                      className="flex items-center gap-2"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      <span className="text-xs md:text-sm text-muted-foreground">
                        {feature}
                      </span>
                    </motion.div>
                  ))}
                </div>

                {/* CTA Button */}
                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onServiceClick(service?.id)}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="text-primary hover:text-primary"
                  >
                    Learn More
                  </Button>
                </motion.div>

                {/* Decorative Corner Element */}
                <motion.div
                  className="absolute top-0 right-0 w-24 h-24 md:w-32 md:h-32 bg-gradient-to-bl from-primary/5 to-transparent rounded-bl-full"
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.5, 0.3]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Services CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-10 md:mt-12 lg:mt-16"
        >
          <Button
            variant="outline"
            size="lg"
            onClick={() => onServiceClick('all')}
            iconName="Grid"
            iconPosition="left"
            className="text-base md:text-lg"
          >
            View All Services
          </Button>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ServicePreview;